import React from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@material-ui/core'
import { get } from 'lodash'
import MessageOutlinedIcon from '@material-ui/icons/MessageOutlined'
import EventNoteOutlined from '@material-ui/icons/EventNoteOutlined'
import SyncAltIcon from '@material-ui/icons/SyncAlt'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { makeStyles } from '@material-ui/core/styles'
import { inviteToPlatformMutation } from '../../mutations'
import { useAuth } from 'lib/auth'
import { gtmEvent } from 'lib/gtm'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import StreamSendMessageButton, {
  MaintenanceSendMessageButton,
} from 'components/stream-messaging/send-message-button'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'

const useStyles = makeStyles(theme => ({
  button: {
    borderColor: 'rgba(0, 0, 0, 0.23)',
    minWidth: '110px',
    boxSizing: 'border-box !important',
    [theme.breakpoints.down('sm')]: {
      borderColor: 'rgba(224, 224, 224, 1)',
    },
    [theme.breakpoints.only('sm')]: {
      maxHeight: '34px',
    },
    [theme.breakpoints.only('xs')]: {
      display: 'block',
      minWidth: '95px', // <-- Make both values 75px on addition of 4th button.
      maxWidth: '95px',
      minHeight: '95px', // <-- Make both values 72px on addition of 4th button.
      maxHeight: '95px',
      fontSize: '12px',
      paddingRight: 0,
      paddingLeft: 0,
    },
    [theme.breakpoints.down('375')]: {
      minWidth: '28%',
      width: '28%',
      minHeight: '54px',
      height: '25vw',
      fontSize: '3.25vw',
      paddingRight: 0,
      paddingLeft: 0,
    },
    // Below should be used when notes button is added.
    // Delete the above at that point.
    // [theme.breakpoints.down(375)]: {
    //   minWidth: '22%',
    //   maxWidth: '22%',
    //   minHeight: '4.5rem',
    //   maxHeight: '4.5rem',
    //   fontSize: '3.25vw',
    //   paddingRight: 0,
    //   paddingLeft: 0,
    // },
  },
  icon: {
    minWidth: '35px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '20px',
    },
    [theme.breakpoints.down(375)]: {
      minWidth: 'unset',
      width: '25%',
    },
  },
}))

export default function StudentActionButtons({ student }) {
  const classes = useStyles()
  const { user } = useAuth()
  const { contact } = student

  const [isMessageButtonEnabled, enabledMessagingService] = useFeatureFlags([
    flags.FLAG_STUDENT_MESSAGE_BUTTON,
    flags.FLAG_MESSAGING_SERVICE,
  ])

  // ---> INVITATION HANDLERS.
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)

  const [inviteToPlatform] = useMutation(inviteToPlatformMutation)

  const handleInvitation = async () => {
    try {
      const { data } = await inviteToPlatform({
        variables: { studentIds: [student.id] },
      })
      const success = get(data, ['SendConnectionInvitation', 'success'])
      if (!success) return snackbarError()
      snackbarSuccess(`${student.firstName} invited to platform`)
    } catch (e) {
      snackbarError()
    }
  }

  // ----> BOOKING HANDLER.
  const handleBookSessionClick = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
    window.open(
      new URL(user.coach.bookingUrl).pathname,
      '_blank',
      'noopener,noreferrer',
    )
  }

  // ----> MESSAGE HANDLERS.
  const sendMessageButtonProps = {
    variant: 'outlined',
    color: 'primary',
    className: classes.button,
    startIcon: <MessageOutlinedIcon className={classes.icon} />,
    children: 'Message',
  }

  const SendMessageButton = (() => {
    switch (enabledMessagingService) {
      case 'stream':
        return () => (
          <SensitiveInfoWrapper>
            <StreamSendMessageButton
              userIds={[contact.externalId]}
              {...sendMessageButtonProps}
            />
          </SensitiveInfoWrapper>
        )
      case 'maintenance':
      default:
        return () => (
          <MaintenanceSendMessageButton {...sendMessageButtonProps} />
        )
    }
  })()

  return (
    <>
      {isMessageButtonEnabled ? <SendMessageButton /> : null}
      <Button
        variant="outlined"
        color="primary"
        className={classes.button}
        startIcon={<EventNoteOutlined className={classes.icon} />}
        onClick={handleBookSessionClick}
      >
        Book
      </Button>
      <Button
        variant="outlined"
        color="primary"
        className={classes.button}
        startIcon={<SyncAltIcon className={classes.icon} />}
        onClick={handleInvitation}
      >
        Invite
      </Button>
    </>
  )
}
